import { AppDispatch, RootState } from '@/store';
import { TDuplicateType, TSimulationMenuOptions, WipTree } from '@/store/types';
import {
  Backdrop,
  Button,
  ButtonIcon,
  Checkbox,
  DatePicker,
  Label,
  Menu,
  MenuOption,
  Radio,
  RadioGroup,
  Slider,
  Stack,
  Typography,
} from '@data-products-and-ai/react-components';
import { TableRowData } from '@data-products-and-ai/react-components/lib/components/DataDisplay/Table/types';
import { TAnchorEl } from '@data-products-and-ai/react-components/lib/components/Layout/Menu/Menu';
import { useDispatch, useSelector } from 'react-redux';

import { SimulationCtx } from '@/contexts/Simulation/SimulationCtx';
import {
  deleteRow,
  disableRow,
  duplicateItem,
  duplicateRow,
  update,
} from '@/store/storeSlice';
import React, { useContext, useEffect, useState } from 'react';

type TContextMenu = {
  anchorEl: TAnchorEl;
  selectedRow: TableRowData | null;
  onClose?: () => void;
  showRemove?: boolean;
  showDuplicate?: boolean;
  showDelete?: boolean;
  showEdit?: boolean;
  allowDisable?: boolean;
};

type TDuplicate = {
  num?: number;
  start_date?: number;
  end_date?: number;
  duplicateType?: TDuplicateType;
};

/**
 * ContextMenu Component
 * Renders a context menu with options to edit, duplicate, remove, or delete a row
 * Manages the state for duplicating multiple rows and handles various actions
 *
 * @param {TContextMenu} props - The properties for the context menu
 */
const ContextMenu = ({
  anchorEl,
  selectedRow,
  onClose,
  showRemove = true,
  showDelete = true,
  showDuplicate = true,
  showEdit = true,
  allowDisable = true,
}: TContextMenu) => {
  const initialDuplicate: TDuplicate = {
    num: 1,
    start_date: 0,
    end_date: 0,
    duplicateType: 'Even',
  };

  const dispatch: AppDispatch = useDispatch();
  const selectedScenario = useSelector((state: RootState) =>
    state.store.Simulation.scenarios.find((item) => item.is_selected === true),
  );
  const open = Boolean(anchorEl);

  const [duplicatedMany, setDuplicatedMany] = useState<boolean>(false);
  const [duplicateObj, setDuplicateObj] =
    useState<TDuplicate>(initialDuplicate);
  const { setSimulationParams } = useContext(SimulationCtx);
  const [replaceWorkplanWithDuplicate, setReplaceWorkplanWithDuplicate] =
    useState(false);

  /**
   * Handles the change of input fields in the duplicate dialog
   * Updates the duplicate object state with the new input value
   *
   * @param {number | string | null} value - The new input value
   * @param {keyof TDuplicate} key - The key of the duplicate object to update
   */
  const handleInputChange = (
    value: number | string | null,
    key: keyof TDuplicate,
  ) => {
    setDuplicateObj((prevForm) => ({
      ...prevForm,
      [key]: value,
    }));
  };

  useEffect(() => {
    setDuplicatedMany(false);

    if (
      !selectedRow ||
      selectedScenario?.data.production_orders.allIds.length === 0
    )
      return;
    if (selectedRow.area === 'production_orders') {
      setDuplicateObj({
        num: 1,
        start_date:
          selectedScenario && selectedRow
            ? selectedScenario?.data.production_orders.byId[selectedRow.id]
                .PO_START_DATE_TIMESTAMP.value
            : 0,
        end_date:
          selectedScenario && selectedRow
            ? selectedScenario?.data.production_orders.byId[selectedRow.id]
                .PO_END_DATE_TIMESTAMP.value
            : 0,
        duplicateType: 'Even',
      });
    }
  }, [anchorEl, selectedRow, selectedScenario]);

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setDuplicatedMany(false);
        handleMenuClose();
      }
    };

    document.addEventListener('keydown', handleKeyPress);

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Handles the edit row action
   * Opens the drawer and sets the form item and area in the simulation parameters
   */
  const handleEditRow = () => {
    if (!selectedRow) return;

    setSimulationParams((prevState) => ({
      ...prevState,
      drawerOpen: true,
      formItem: selectedRow.id,
      formArea: selectedRow.area as TSimulationMenuOptions,
    }));
  };

  /**
   * Handles the delete row action
   * Dispatches the delete row action with the selected row ID and area
   */
  const handleDeleteRow = () => {
    if (!selectedRow) return;

    dispatch(
      deleteRow({
        id: selectedRow.id,
        area: selectedRow.area as keyof WipTree,
      }),
    );
  };
  const handleDisableRow = () => {
    if (!selectedRow) return;
    console.log({
      id: selectedRow.id,
      isDisabled: !selectedRow.isDisabled,
      area: selectedRow.area as keyof WipTree,
    });

    dispatch(
      disableRow({
        id: selectedRow.id,
        isDisabled: !selectedRow.isDisabled,
        area: selectedRow.area as keyof WipTree,
      }),
    );
  };

  const handleShowDuplicateManyRow = () => {
    if (!selectedRow) return;

    setDuplicatedMany(true);
  };

  /**
   * Handles the duplicate many rows action
   * Dispatches the duplicate row action with the duplicate object and selected row details
   *
   * @param {TDuplicate} obj - The duplicate object containing the duplication details
   */
  const handleDuplicateManyRow = (obj: TDuplicate) => {
    if (!selectedRow) return;
    console.log({
      id: selectedRow.id,
      area: selectedRow.area as keyof WipTree,
    });

    if (selectedRow.area === 'production_orders') {
      dispatch(
        duplicateRow({
          id: selectedRow.id,
          content: {
            num: obj.num ?? 1,
            duplicateType: obj.duplicateType,
            intervalStart: obj.start_date,
            intervalEnd: obj.end_date,
          },
          area: selectedRow.area as keyof WipTree,
        }),
      );
    }
    else {
      dispatch(
        duplicateRow({
          id: selectedRow.id,
          content: {
            duplicateType: obj.duplicateType,
            num: obj.num ?? 1,
          },
          area: selectedRow.area as keyof WipTree,
          toReplace: selectedRow.area === 'work_plans' ? replaceWorkplanWithDuplicate: false,
        }),
      );
    }

    handleMenuClose();
  };

  /**
   * Handles the duplicate item action
   * Dispatches the duplicate item action with the selected row ID, area, and ID key
   */
  const handleDuplicateItem = () => {
    if (!selectedRow) return;
    console.log({
      id: selectedRow.id,
      area: selectedRow.area as keyof WipTree,
    });

    const selected_area = selectedRow.area as keyof WipTree;
    let id_key;

    if (selected_area === 'resources') id_key = 'RESOURCE_ID';
    if (selected_area === 'operations') id_key = 'OPERATION_ID';
    if (selected_area === 'shifts') id_key = 'SHIFT_ID';
    if (selected_area === 'shutdowns') id_key = 'SHUTDOWN_ID';
    if (selected_area === 'work_plans') id_key = 'WORK_PLAN_ID';

    if (id_key)
      dispatch(
        duplicateItem({
          id: selectedRow.id,
          id_key: id_key,
          area: selectedRow.area as keyof WipTree,
        }),
      );
    handleMenuClose();
  };

  const handleMenuClose = () => {
    //setDuplicatedMany(false);
    //setDuplicationNumber(1);
    onClose && onClose();
    //setAnchorEl(null);
  };

  return !duplicatedMany ? (
    <Menu
      open={open}
      onClose={handleMenuClose}
      anchorEl={anchorEl}
      closeOnClickOutside={!duplicatedMany}
    >
      {showEdit && (
        <MenuOption
          onClick={handleEditRow}
          onAfterClick={handleMenuClose}
          verticalPadding={5}
          horizontalPadding={15}
        >
          Edit
        </MenuOption>
      )}
      {showDuplicate && selectedRow && (
        <MenuOption
          onClick={
            selectedRow?.area === 'production_orders' ||
            selectedRow?.area === 'work_plans' ||
            selectedRow?.area === 'resource_sub_classes'
              ? () => handleShowDuplicateManyRow()
              : () => handleDuplicateItem()
          }
          verticalPadding={5}
          horizontalPadding={15}
        >
          Duplicate
        </MenuOption>
      )}
      {showRemove && allowDisable && (
        <MenuOption
          onClick={() => handleDisableRow()}
          onAfterClick={handleMenuClose}
          verticalPadding={5}
          horizontalPadding={15}
        >
          {selectedRow?.isDisabled ? 'Restore' : 'Remove'}
        </MenuOption>
      )}

      {(selectedRow?.isDisabled || !allowDisable) && showDelete && (
        <MenuOption
          onClick={() => handleDeleteRow()}
          onAfterClick={handleMenuClose}
          verticalPadding={5}
          horizontalPadding={15}
        >
          <div style={{ color: '#FF0000' }}>Delete Permanently</div>
        </MenuOption>
      )}
    </Menu>
  ) : selectedRow?.area === 'production_orders' ? (
    <React.Fragment>
      <Backdrop backgroundColor="light" isShown />

      <div
        style={{
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          position: 'fixed',
          zIndex: 22000,
          backgroundColor: '#FFFFFF',
          border: 'solid 1px #CCCCCC',
          borderRadius: 4,
          boxShadow: '3px 3px 10px rgba(0, 0, 0, 0.15)',
          padding: 40,
        }}
      >
        <div style={{ position: 'absolute', top: 0, right: 0, opacity: 0.3 }}>
          <ButtonIcon
            icon="IconClose"
            size="extra-small"
            onClick={() => {
              setDuplicatedMany(false);
              handleMenuClose();
            }}
          />
        </div>

        <Stack gap={5}>
          <Typography tag="textsmall_strong">
            {selectedRow?.data.title}
          </Typography>
          <Typography tag="textsmall">
            Create {duplicateObj.num ?? 1} duplication
            {(duplicateObj.num ?? 1) > 1 && 's'}
          </Typography>

          <div style={{ padding: 15 }}>
            <Slider
              defaultValue={duplicateObj.num}
              min={1}
              max={50}
              width={300}
              showValue={false}
              onChange={(event) => handleInputChange(event.target.value, 'num')}
            />
          </div>

          <div>
            <Label>Distribute delivery date from:</Label>
            <Stack direction="row" gap={10}>
              <div style={{ width: 150 }}>
                <DatePicker
                  id="Duplicate_StartDate"
                  size="small"
                  defaultValue={
                    selectedRow
                      ? duplicateObj.start_date /* selectedScenario?.data.production_orders.byId[
                            selectedRow.id
                          ].PO_START_DATE_TIMESTAMP.value */
                      : undefined
                  }
                  onChange={(value) => handleInputChange(value, 'start_date')}
                />
              </div>
              <div style={{ width: 150 }}>
                <DatePicker
                  id="Duplicate_EndDate"
                  size="small"
                  defaultValue={
                    selectedRow
                      ? duplicateObj.end_date /* selectedScenario?.data.production_orders.byId[
                            selectedRow.id
                          ].PO_END_DATE_TIMESTAMP.value */
                      : undefined
                  }
                  onChange={(value) => handleInputChange(value, 'end_date')}
                />
              </div>
            </Stack>
          </div>
          <div style={{ padding: 10 }}>
            <Typography tag="textsmall">
              <RadioGroup
                id="SpreadType"
                orientation="row"
                gap={20}
                onChange={(event) =>
                  handleInputChange(event.target.value, 'duplicateType')
                }
              >
                <Radio
                  name="SpreadType"
                  isChecked={duplicateObj.duplicateType === 'Even'}
                  label="Evenly"
                  value="Even"
                />

                <Radio
                  name="SpreadType"
                  isChecked={duplicateObj.duplicateType === 'Random'}
                  label="Randomly"
                  value="Random"
                />
              </RadioGroup>
            </Typography>
          </div>

          <Button
            type="secondary"
            width={200}
            onClick={() => handleDuplicateManyRow(duplicateObj)}
          >
            Confirm
          </Button>
        </Stack>
      </div>
    </React.Fragment>
  ) : (
    (selectedRow?.area === 'work_plans' ||
      selectedRow?.area === 'resource_sub_classes') && (
      <React.Fragment>
        <Backdrop backgroundColor="light" isShown />
        <div
          style={{
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            position: 'fixed',
            zIndex: 22000,
            backgroundColor: '#FFFFFF',
            border: 'solid 1px #CCCCCC',
            borderRadius: 4,
            boxShadow: '3px 3px 10px rgba(0, 0, 0, 0.15)',
            padding: 40,
          }}
        >
          <div style={{ position: 'absolute', top: 0, right: 0, opacity: 0.3 }}>
            <ButtonIcon
              icon="IconClose"
              size="extra-small"
              onClick={() => {
                setDuplicatedMany(false);
                handleMenuClose();
              }}
            />
          </div>

          <Stack gap={15}>
            <Typography tag="textdefault_strong">
              {
                selectedRow?.data[
                  selectedRow?.area === 'work_plans'
                    ? 'OPERATION'
                    : 'RESOURCE_NAME'
                ]
              }
            </Typography>
            <Typography tag="textsmall">
              Are you sure you want to duplicate this?
              </Typography>
            {selectedRow?.area === 'work_plans' && (
              <Typography tag="textsmall">
                <Checkbox
                  id="Duplicate_Operations"
                  label="Replace original workplan with duplicate on all production orders"
                  onChange={(event) =>
                    setReplaceWorkplanWithDuplicate(event.target.checked)
                  }
                  isChecked={replaceWorkplanWithDuplicate}
                />
              </Typography>
            )}

            <Button
              type="secondary"
              width={200}
              onClick={() => handleDuplicateManyRow(duplicateObj)}
            >
              Confirm
            </Button>
          </Stack>
        </div>
      </React.Fragment>
    )
  );
};

export default ContextMenu;
